import React, { Component } from 'react'
import moment from 'moment'
import { ReactSVG } from 'react-svg'
import { BnSubpage } from '../Page'
import Back from '../../assets/Icons/Back.svg'
import Forward from '../../assets/Icons/Forward.svg'
import './index.css'

const CATEGORIES = [
  { id: 'swipes', label: 'Swipes', unit: 'swipes' },
  { id: 'suggestions', label: 'Suggestions', unit: 'inferences' },
  { id: 'edits', label: 'Edits', unit: 'edits' },
  { id: 'writes', label: 'Writing', unit: 'writes', hidden: true },
  { id: 'chat', label: 'Chat', unit: 'responses', hidden: true },
  { id: 'speech', label: 'Speech', unit: 'requests' },
  { id: 'language', label: 'Language and categories', unit: 'inferences' },
]

// Retired features remain in historical totals, but not in the category lists.
const VISIBLE_CATEGORIES = CATEGORIES.filter(category => !category.hidden)

const emptyMetrics = () => ({
  requests: 0,
  count: 0,
  promptTokens: 0,
  uncachedInputTokens: 0,
  cachedInputTokens: 0,
  cacheWriteTokens: 0,
  outputTokens: 0,
  reasoningTokens: 0,
  totalTokens: 0,
  costUsdMicros: 0,
  unpricedTokens: 0,
  missingUsageRequests: 0
})

const emptyCategories = () => CATEGORIES.reduce((result, category) => {
  result[category.id] = emptyMetrics()
  return result
}, {})

const addMetrics = (target, source = {}) => {
  Object.keys(target).forEach(key => {
    target[key] += Number(source[key]) || 0
  })
  return target
}

const aggregateBuckets = buckets => {
  const categories = emptyCategories()
  buckets.forEach(bucket => {
    CATEGORIES.forEach(category => {
      addMetrics(categories[category.id], (bucket.categories || {})[category.id])
    })
  })
  return categories
}

const totalMetrics = categories => CATEGORIES.reduce(
  (total, category) => addMetrics(total, categories[category.id]),
  emptyMetrics()
)

const periodRange = (date, view) => {
  const value = moment(date).local()
  if (view === 'day') {
    return {
      start: value.clone().startOf('day'),
      end: value.clone().add(1, 'day').startOf('day')
    }
  }
  if (view === 'week') {
    return {
      start: value.clone().startOf('isoWeek'),
      end: value.clone().startOf('isoWeek').add(1, 'week')
    }
  }
  return {
    start: value.clone().startOf('month'),
    end: value.clone().startOf('month').add(1, 'month')
  }
}

const loadRange = date => ({
  start: moment(date).local().startOf('month').subtract(8, 'days'),
  end: moment(date).local().startOf('month').add(1, 'month').add(8, 'days')
})

const formatTokens = value => {
  const tokens = Number(value) || 0
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(tokens >= 10000000 ? 1 : 2)}M`
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(tokens >= 100000 ? 0 : 1)}K`
  return Math.round(tokens).toLocaleString()
}

const formatCost = micros => {
  const dollars = (Number(micros) || 0) / 1000000
  if (dollars === 0) return '$0.00'
  if (dollars >= 0.01) return `$${dollars.toFixed(2)}`
  if (dollars >= 0.001) return `$${dollars.toFixed(4)}`
  return `$${dollars.toFixed(6)}`
}

const displayedCost = metrics => metrics.unpricedTokens > 0
  ? (metrics.costUsdMicros > 0 ? `Partial ${formatCost(metrics.costUsdMicros)}` : 'Unpriced')
  : formatCost(metrics.costUsdMicros)

const periodLabel = (date, view) => {
  const range = periodRange(date, view)
  if (view === 'day') return range.start.format('dddd, MMMM D, YYYY')
  if (view === 'week') {
    const lastDay = range.end.clone().subtract(1, 'day')
    return `${range.start.format('MMM D')} – ${lastDay.format('MMM D, YYYY')}`
  }
  return range.start.format('MMMM YYYY')
}

class UsageCalendar extends Component {
  changePeriod = amount => {
    this.props.onPageChange(moment(this.props.date).add(amount, this.props.view).toDate())
  }

  days = () => {
    if (this.props.view === 'day') return [moment(this.props.date).startOf('day')]
    if (this.props.view === 'week') {
      const first = moment(this.props.date).startOf('isoWeek')
      return Array.from({ length: 7 }, (_, index) => first.clone().add(index, 'day'))
    }
    const first = moment(this.props.date).startOf('month').startOf('isoWeek')
    return Array.from({ length: 42 }, (_, index) => first.clone().add(index, 'day'))
  }

  render () {
    const { activeDays, date, view, onDateChange, onViewChange } = this.props
    const selectedKey = moment(date).format('YYYY-MM-DD')
    const monthKey = moment(date).format('YYYY-MM')
    const periodName = periodLabel(date, view)
    return <div className={'intelliUsageCalendar intelliUsageCalendarMode' + view.substring(0, 1).toUpperCase() + view.substring(1)}>
      <div className='intelliUsageViewSelector'>
        {['day', 'week', 'month'].map(choice => (
          <button
            type='button'
            key={choice}
            className={'intelliUsageViewChoice' + (view === choice ? ' intelliUsageViewChoiceSelected' : '')}
            onClick={() => onViewChange(choice)}>
            {choice.substring(0, 1).toUpperCase() + choice.substring(1)}
          </button>
        ))}
      </div>
      <div className='intelliUsageCalendarHeader'>
        <button type='button' aria-label={'Previous ' + view} onClick={() => this.changePeriod(-1)}><ReactSVG src={Back} /></button>
        <div>{periodName}</div>
        <button type='button' aria-label={'Next ' + view} onClick={() => this.changePeriod(1)}><ReactSVG src={Forward} /></button>
      </div>
      {view !== 'day' && <div className='intelliUsageCalendarGrid intelliUsageCalendarWeekdays'>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => <div key={day}>{day}</div>)}
      </div>}
      <div className={'intelliUsageCalendarGrid intelliUsageCalendarDays intelliUsageCalendarDays' + view.substring(0, 1).toUpperCase() + view.substring(1)}>
        {this.days().map(day => {
          const key = day.format('YYYY-MM-DD')
          let className = 'intelliUsageCalendarDay'
          if (view === 'month' && day.format('YYYY-MM') !== monthKey) className += ' intelliUsageCalendarDayOutside'
          if (key === moment().format('YYYY-MM-DD')) className += ' intelliUsageCalendarDayToday'
          if (activeDays.has(key)) className += ' intelliUsageCalendarDayActive'
          if (key === selectedKey) className += ' intelliUsageCalendarDaySelected'
          return <button type='button' key={key} className={className} onClick={() => onDateChange(day.toDate())}>
            <span>
              <strong>{day.format('D')}</strong>
              {view === 'day' && <small>{day.format('dddd')}</small>}
            </span>
          </button>
        })}
      </div>
    </div>
  }
}

const CategoryRow = ({ category, metrics, compact }) => (
  <div className={'intelliUsageCategory' + (compact ? ' intelliUsageCategoryCompact' : '')}>
    <div className='intelliUsageCategoryIdentity'>
      <div className='intelliUsageCategoryLabel'>{category.label}</div>
      <div className='intelliUsageCategoryCount'>{Math.round(metrics.count).toLocaleString()} {category.unit}</div>
    </div>
    <div className='intelliUsageCategoryValue'>
      <div>{formatTokens(metrics.totalTokens)} tokens</div>
      <div className='intelliUsageCategoryCost'>{displayedCost(metrics)}</div>
    </div>
  </div>
)

const DailyUsage = ({ day, categories }) => {
  const total = totalMetrics(categories)
  return <div className='intelliUsageDay'>
    <div className='intelliUsageDayHeader'>
      <div>{day.format('ddd, MMM D')}</div>
      <div className='intelliUsageDayTotal'>{formatTokens(total.totalTokens)} tokens · {displayedCost(total)}</div>
    </div>
    <div className='intelliUsageDayCategories'>
      {VISIBLE_CATEGORIES.map(category => (
        <CategoryRow key={category.id} category={category} metrics={categories[category.id]} compact />
      ))}
    </div>
  </div>
}

export class Usage extends BnSubpage {
  constructor (props) {
    super(props)
    this.state = {
      ...this.state,
      buckets: [],
      currentDate: new Date(),
      view: 'month',
      loading: true,
      error: null
    }
  }

  componentDidMount () {
    this.observeUsage(this.state.currentDate)
  }

  componentWillUnmount () {
    this.unmounted = true
    if (this.retryUsageTimer) clearTimeout(this.retryUsageTimer)
    if (this.unsubscribeUsage) this.unsubscribeUsage()
  }

  observeUsage = date => {
    const observationId = (this.observationId || 0) + 1
    this.observationId = observationId
    const range = loadRange(date)
    if (this.retryUsageTimer) clearTimeout(this.retryUsageTimer)
    if (this.unsubscribeUsage) this.unsubscribeUsage()
    this.loadedRange = range
    this.setState({ loading: true, error: null })
    this.unsubscribeUsage = this.props.me.observeUsageAnalytics(
      {
        from: range.start.valueOf(),
        to: range.end.valueOf()
      },
      buckets => {
        if (this.unmounted || observationId !== this.observationId) return
        this.setState({ buckets, loading: false, error: null })
      },
      error => {
        if (this.unmounted || observationId !== this.observationId) return
        this.setState({
          loading: false,
          error: error.message || String(error)
        })
        this.retryUsageTimer = setTimeout(() => {
          if (!this.unmounted && observationId === this.observationId) {
            this.observeUsage(this.state.currentDate)
          }
        }, 2000)
      }
    )
  }

  ensureRange = date => {
    if (!this.loadedRange || !moment(date).isBetween(this.loadedRange.start, this.loadedRange.end, 'day', '[]')) {
      this.observeUsage(date)
    }
  }

  onDateChange = date => {
    this.setState({ currentDate: date })
    this.ensureRange(date)
  }

  onPageChange = date => {
    this.setState({ currentDate: date })
    this.observeUsage(date)
  }

  onViewChange = view => {
    this.setState({ view })
  }

  bucketsForRange = range => this.state.buckets.filter(bucket => {
    const timestamp = Number(bucket.bucketStart)
    return timestamp >= range.start.valueOf() && timestamp < range.end.valueOf()
  })

  dayGroups = range => {
    const result = []
    for (let day = range.end.clone().subtract(1, 'day'); !day.isBefore(range.start); day.subtract(1, 'day')) {
      const dayRange = { start: day.clone(), end: day.clone().add(1, 'day') }
      const categories = aggregateBuckets(this.bucketsForRange(dayRange))
      const total = totalMetrics(categories)
      if (total.count <= 0 && total.totalTokens <= 0) continue
      result.push({
        day: day.clone(),
        categories
      })
    }
    return result
  }

  activeDays = () => {
    const days = new Set()
    this.state.buckets.forEach(bucket => {
      const date = moment(Number(bucket.bucketStart)).local()
      const key = date.format('YYYY-MM-DD')
      const total = totalMetrics(aggregateBuckets([bucket]))
      if (total.count > 0 || total.totalTokens > 0) days.add(key)
    })
    return days
  }

  renderContent () {
    const { currentDate, view, loading, error } = this.state
    const range = periodRange(currentDate, view)
    const categories = aggregateBuckets(this.bucketsForRange(range))
    const total = totalMetrics(categories)
    const interruptedRequests = Math.max(0, total.requests - total.count)
    const missingCompletedUsage = Math.max(0, total.missingUsageRequests - interruptedRequests)
    const days = this.dayGroups(range)

    return <div className='intelliUsageView bnSubpageTopLevel'>
      <div className='intelliUsageCalendarPanel'>
        <UsageCalendar
          activeDays={this.activeDays()}
          date={currentDate}
          view={view}
          onDateChange={this.onDateChange}
          onPageChange={this.onPageChange}
          onViewChange={this.onViewChange}
        />
      </div>

      <div className='intelliUsageSummary'>
        <div className='intelliUsagePeriod'>{periodLabel(currentDate, view)}</div>
        <div className='intelliUsageTotal'>
          <div>
            <div className='intelliUsageTotalLabel'>Total</div>
            <div className='intelliUsageTotalOperations'>{Math.round(total.count).toLocaleString()} operations</div>
          </div>
          <div className='intelliUsageTotalValue'>{formatTokens(total.totalTokens)} tokens · {displayedCost(total)}</div>
        </div>

        {loading && <div className='intelliUsageMessage'>Loading usage…</div>}
        {error && <div className='intelliUsageMessage intelliUsageError'>{error}</div>}

        {!loading && !error && <div className='intelliUsageCategories'>
          {VISIBLE_CATEGORIES.map(category => (
            <CategoryRow key={category.id} category={category} metrics={categories[category.id]} />
          ))}
        </div>}

        {!loading && !error && view !== 'day' && <div className='intelliUsageDaily'>
          <div className='intelliUsageDailyTitle'>Per {view}</div>
          {days.map(item => <DailyUsage key={item.day.format('YYYY-MM-DD')} {...item} />)}
        </div>}

        <div className='intelliUsageFootnote'>
          Suggestions count completed inferences on the phone and server, not alternatives returned. Dollar values are estimates using configured API list prices. Connected subscriptions do not consume token-pack tokens. Private usage reports contain aggregate counts, token totals, and model IDs, without inference text or credentials.
          {missingCompletedUsage > 0 && <div>Token totals are incomplete: {missingCompletedUsage.toLocaleString()} completed {missingCompletedUsage === 1 ? 'request' : 'requests'} did not report usage.</div>}
          {interruptedRequests > 0 && <div>Interrupted requests may not include final token usage.</div>}
          {total.unpricedTokens > 0 && <div>Cost excludes {formatTokens(total.unpricedTokens)} tokens without configured pricing.</div>}
        </div>
      </div>
    </div>
  }
}
